import React, { useState, useMemo } from 'react'
import { Search, Plus, Users, Edit, Trash2, Calendar, Clock, Building2, Wand2, ClipboardList, RefreshCw, X, AlertTriangle } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Select,
  SelectOption,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Pagination,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui'
import { LoadingTable } from '../components/ui/loading-states'
import { ErrorState } from '../components/ui/error-state'
import {
  useDailyStaffingLimits,
  useMonthlyStaffingLimits,
  useDeleteStaffingLimit,
  useGenerateShiftLimits,
  type DailyStaffingLimit,
  type MonthlyStaffingLimit,
  type DailyStaffingLimitFilters,
  type MonthlyStaffingLimitFilters,
  type GenerateShiftLimitsResponse,
} from '../hooks/useStaffingLimits'
import { usePositions } from '../hooks/usePositions'
import { useDepartmentsForSelect } from '../hooks/useDepartments'
import { useOrganizationsForSelect } from '../hooks/organizations/useOrganizations'
import {
  useStaffingSchedule,
  type StaffingScheduleFilters,
} from '../hooks/useStaffingSchedule'
import { DailyStaffingLimitForm } from '../components/staffing-limits/DailyStaffingLimitForm'
import { MonthlyStaffingLimitForm } from '../components/staffing-limits/MonthlyStaffingLimitForm'
import { DeleteStaffingLimitDialog } from '../components/staffing-limits/DeleteStaffingLimitDialog'
import { GenerateMonthlyLimitsDialog } from '../components/staffing-limits/GenerateMonthlyLimitsDialog'

type LimitType = 'daily' | 'monthly' | 'schedule'

const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
]

export function StaffingLimits() {
  const [activeTab, setActiveTab] = useState<LimitType>('daily')

  // Get current month boundaries for default filter
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]
  const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0]

  // Daily filters
  const [dailyFilters, setDailyFilters] = useState<DailyStaffingLimitFilters>({
    page: 1,
    limit: 20,
    sort: 'date:asc',
    dateFrom: firstDayOfMonth,
    dateTo: lastDayOfMonth,
  })

  // Monthly filters
  const [monthlyFilters, setMonthlyFilters] = useState<MonthlyStaffingLimitFilters>({
    page: 1,
    limit: 20,
    sort: 'year:desc',
    year: currentYear,
  })

  // Schedule filters
  const [scheduleFilters, setScheduleFilters] = useState<StaffingScheduleFilters>({
    page: 1,
    limit: 20,
    sort: 'date:desc',
    isActive: true,
  })

  const [searchTerm, setSearchTerm] = useState('')
  const [positionFilter, setPositionFilter] = useState<string>('')
  const [departmentFilter, setDepartmentFilter] = useState<string>('')
  const [organizationFilter, setOrganizationFilter] = useState<string>('')

  // Modal states
  const [isCreateDailyModalOpen, setIsCreateDailyModalOpen] = useState(false)
  const [isCreateMonthlyModalOpen, setIsCreateMonthlyModalOpen] = useState(false)
  const [isEditDailyModalOpen, setIsEditDailyModalOpen] = useState(false)
  const [isEditMonthlyModalOpen, setIsEditMonthlyModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false)
  const [selectedDailyLimit, setSelectedDailyLimit] = useState<DailyStaffingLimit | null>(null)
  const [selectedMonthlyLimit, setSelectedMonthlyLimit] = useState<MonthlyStaffingLimit | null>(null)
  const [limitToDelete, setLimitToDelete] = useState<{ id: string; type: LimitType } | null>(null)
  const [shiftGenResult, setShiftGenResult] = useState<GenerateShiftLimitsResponse | null>(null)
  const [shiftGenError, setShiftGenError] = useState<string | null>(null)

  // Data hooks
  const { data: dailyData, isLoading: dailyLoading, error: dailyError, refetch: refetchDaily } = useDailyStaffingLimits(dailyFilters)
  const { data: monthlyData, isLoading: monthlyLoading, error: monthlyError, refetch: refetchMonthly } = useMonthlyStaffingLimits(monthlyFilters)
  const { data: positionsData } = usePositions({ limit: 100 })
  const { data: organizationsData } = useOrganizationsForSelect()
  // Подразделения фильтруются по выбранной организации
  const { data: departmentsData } = useDepartmentsForSelect(organizationFilter || undefined)
  const deleteLimit = useDeleteStaffingLimit()
  const generateShiftLimits = useGenerateShiftLimits()
  const { data: scheduleData, isLoading: scheduleLoading, error: scheduleError, refetch: refetchSchedule } = useStaffingSchedule(scheduleFilters)

  // Генерация лимитов смен из штатки 1С за выбранный (или текущий) период
  const handleGenerateFromStaffing = async () => {
    const year = monthlyFilters.year || currentYear
    const month = monthlyFilters.month || currentMonth
    setShiftGenError(null)
    setShiftGenResult(null)
    try {
      const result = await generateShiftLimits.mutateAsync({ year, month })
      setShiftGenResult(result)
    } catch (error) {
      setShiftGenError(
        (error as { message?: string })?.message || 'Не удалось рассчитать лимиты смен'
      )
    }
  }

  // Handlers for schedule filters
  const handleSchedulePageChange = (page: number) => {
    setScheduleFilters(prev => ({ ...prev, page }))
  }

  const handleScheduleActiveFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setScheduleFilters(prev => ({
      ...prev,
      page: 1,
      isActive: value === '' ? undefined : value === 'true',
    }))
  }

  // Handlers for daily filters
  const handleDailyPageChange = (page: number) => {
    setDailyFilters(prev => ({ ...prev, page }))
  }

  const handleDailyDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDailyFilters(prev => ({ ...prev, page: 1, dateFrom: e.target.value || undefined }))
  }

  const handleDailyDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDailyFilters(prev => ({ ...prev, page: 1, dateTo: e.target.value || undefined }))
  }

  // Handlers for monthly filters
  const handleMonthlyPageChange = (page: number) => {
    setMonthlyFilters(prev => ({ ...prev, page }))
  }

  const handleMonthlyYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value ? parseInt(e.target.value, 10) : undefined
    setMonthlyFilters(prev => ({ ...prev, page: 1, year: value }))
  }

  const handleMonthlyMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value ? parseInt(e.target.value, 10) : undefined
    setMonthlyFilters(prev => ({ ...prev, page: 1, month: value }))
  }

  // Common filter handlers
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    if (activeTab === 'daily') {
      setDailyFilters(prev => ({ ...prev, page: 1, search: e.target.value || undefined }))
    } else if (activeTab === 'monthly') {
      setMonthlyFilters(prev => ({ ...prev, page: 1, search: e.target.value || undefined }))
    } else {
      setScheduleFilters(prev => ({ ...prev, page: 1, search: e.target.value || undefined }))
    }
  }

  const handlePositionFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setPositionFilter(value)
    if (activeTab === 'daily') {
      setDailyFilters(prev => ({ ...prev, page: 1, positionId: value || undefined }))
    } else if (activeTab === 'monthly') {
      setMonthlyFilters(prev => ({ ...prev, page: 1, positionId: value || undefined }))
    } else {
      setScheduleFilters(prev => ({ ...prev, page: 1, positionId: value || undefined }))
    }
  }

  const handleDepartmentFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setDepartmentFilter(value)
    if (activeTab === 'daily') {
      setDailyFilters(prev => ({ ...prev, page: 1, departmentId: value || undefined }))
    } else if (activeTab === 'monthly') {
      setMonthlyFilters(prev => ({ ...prev, page: 1, departmentId: value || undefined }))
    } else {
      setScheduleFilters(prev => ({ ...prev, page: 1, departmentId: value || undefined }))
    }
  }

  const handleOrganizationFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setOrganizationFilter(value)
    setDepartmentFilter('')
    if (activeTab === 'daily') {
      setDailyFilters(prev => ({ ...prev, page: 1, organizationId: value || undefined, departmentId: undefined }))
    } else if (activeTab === 'monthly') {
      setMonthlyFilters(prev => ({ ...prev, page: 1, organizationId: value || undefined, departmentId: undefined }))
    }
  }

  // Format helpers
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatYearMonth = (year: number, month: number) => {
    return `${MONTH_NAMES[month - 1]} ${year}`
  }

  // Action handlers
  const handleEditDaily = (limit: DailyStaffingLimit) => {
    setSelectedDailyLimit(limit)
    setIsEditDailyModalOpen(true)
  }

  const handleEditMonthly = (limit: MonthlyStaffingLimit) => {
    setSelectedMonthlyLimit(limit)
    setIsEditMonthlyModalOpen(true)
  }

  const handleDelete = (id: string, type: LimitType) => {
    setLimitToDelete({ id, type })
    setIsDeleteModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (limitToDelete) {
      await deleteLimit.mutateAsync(limitToDelete.id)
      setIsDeleteModalOpen(false)
      setLimitToDelete(null)
    }
  }

  // Statistics
  const dailyStats = useMemo(() => {
    if (!dailyData?.data) return { total: 0, uniquePositions: 0, uniqueOrganizations: 0, totalCapacity: 0 }

    const positions = new Set(dailyData.data.map(l => l.position_id))
    const organizations = new Set(dailyData.data.map(l => l.organization_id))
    const totalCapacity = dailyData.data.reduce((sum, l) => sum + l.max_employees, 0)

    return {
      total: dailyData.total,
      uniquePositions: positions.size,
      uniqueOrganizations: organizations.size,
      totalCapacity,
    }
  }, [dailyData])

  const monthlyStats = useMemo(() => {
    if (!monthlyData?.data) return { total: 0, uniquePositions: 0, uniqueOrganizations: 0, totalHours: 0 }

    const positions = new Set(monthlyData.data.map(l => l.position_id))
    const organizations = new Set(monthlyData.data.map(l => l.organization_id))
    const totalHours = monthlyData.data.reduce((sum, l) => sum + l.max_hours, 0)

    return {
      total: monthlyData.total,
      uniquePositions: positions.size,
      uniqueOrganizations: organizations.size,
      totalHours,
    }
  }, [monthlyData])

  const formatMoney = (value: number | null) => {
    if (value === null) return '—'
    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'KZT', maximumFractionDigits: 0 }).format(value)
  }

  // Schedule statistics
  const scheduleStats = useMemo(() => {
    if (!scheduleData?.data) return { total: 0, totalRates: 0, totalPayroll: 0, activeCount: 0 }

    const totalRates = scheduleData.data.reduce((sum, s) => sum + s.rate_count, 0)
    const totalPayroll = scheduleData.data.reduce((sum, s) => sum + (s.payroll_fund || 0), 0)
    const activeCount = scheduleData.data.filter(s => s.is_active).length

    return {
      total: scheduleData.total,
      totalRates,
      totalPayroll,
      activeCount,
    }
  }, [scheduleData])

  // Generate year options
  const yearOptions = useMemo(() => {
    const years = []
    for (let y = currentYear + 1; y >= currentYear - 5; y--) {
      years.push(y)
    }
    return years
  }, [currentYear])

  const isLoading = activeTab === 'daily' ? dailyLoading : activeTab === 'monthly' ? monthlyLoading : scheduleLoading
  const error = activeTab === 'daily' ? dailyError : activeTab === 'monthly' ? monthlyError : scheduleError
  const refetch = activeTab === 'daily' ? refetchDaily : activeTab === 'monthly' ? refetchMonthly : refetchSchedule

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Лимиты штатного расписания</h1>
          <p className="text-muted-foreground">
            Управление дневными и месячными лимитами
          </p>
        </div>
        <ErrorState
          title="Ошибка загрузки лимитов"
          onAction={refetch}
        />
      </div>
    )
  }

  return (
    <div className="space-y-6 flex-1 overflow-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Лимиты штатного расписания</h1>
          <p className="text-muted-foreground">
            Управление дневными (макс. сотрудников) и месячными (макс. часов) лимитами
          </p>
        </div>
        {activeTab !== 'schedule' && (
          <div className="flex items-center gap-2">
            {activeTab === 'monthly' && (
              <>
                <Button
                  variant="outline"
                  onClick={handleGenerateFromStaffing}
                  disabled={generateShiftLimits.isPending}
                  title="Рассчитать лимиты смен из штатного расписания 1С (ставки × рабочие дни графика)"
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${generateShiftLimits.isPending ? 'animate-spin' : ''}`} />
                  {generateShiftLimits.isPending ? 'Расчёт...' : 'Лимиты смен из 1С'}
                </Button>
                <Button variant="outline" onClick={() => setIsGenerateModalOpen(true)}>
                  <Wand2 className="mr-2 h-4 w-4" />
                  Автоматически создать
                </Button>
              </>
            )}
            <Button onClick={() => activeTab === 'daily' ? setIsCreateDailyModalOpen(true) : setIsCreateMonthlyModalOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Добавить {activeTab === 'daily' ? 'дневной' : 'месячный'} лимит
            </Button>
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as LimitType)}>
        <TabsList>
          <TabsTrigger value="daily">
            <Calendar className="mr-2 h-4 w-4" />
            Дневные лимиты
          </TabsTrigger>
          <TabsTrigger value="monthly">
            <Clock className="mr-2 h-4 w-4" />
            Месячные лимиты
          </TabsTrigger>
          <TabsTrigger value="schedule">
            <ClipboardList className="mr-2 h-4 w-4" />
            Штатное расписание
          </TabsTrigger>
        </TabsList>

        {/* Common Filters */}
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="text-lg">Фильтры</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Organization filter */}
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">
                  Организация
                </label>
                <Select
                  value={organizationFilter}
                  onChange={handleOrganizationFilter}
                >
                  <SelectOption value="">Все организации</SelectOption>
                  {organizationsData?.map((org: { id: string; name: string }) => (
                    <SelectOption key={org.id} value={org.id}>
                      {org.name}
                    </SelectOption>
                  ))}
                </Select>
              </div>

              {/* Position filter */}
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">
                  Должность
                </label>
                <Select
                  value={positionFilter}
                  onChange={handlePositionFilter}
                >
                  <SelectOption value="">Все должности</SelectOption>
                  {positionsData?.data.map((pos) => (
                    <SelectOption key={pos.id} value={pos.id}>
                      {pos.name}
                    </SelectOption>
                  ))}
                </Select>
              </div>

              {/* Department filter */}
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">
                  Отдел
                </label>
                <Select
                  value={departmentFilter}
                  onChange={handleDepartmentFilter}
                  disabled={!organizationFilter}
                >
                  <SelectOption value="">
                    {organizationFilter ? 'Все отделы' : 'Сначала выберите организацию'}
                  </SelectOption>
                  {departmentsData?.map((dept) => (
                    <SelectOption key={dept.id} value={dept.id}>
                      {dept.name}
                    </SelectOption>
                  ))}
                </Select>
              </div>

              {/* Search */}
              <div className="relative">
                <label className="text-sm font-medium text-muted-foreground mb-1 block">
                  Поиск
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Поиск по должности..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="pl-10"
                  />
                </div>
              </div>
            </div>

            {/* Tab-specific filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              {activeTab === 'daily' ? (
                <>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">
                      Дата с
                    </label>
                    <Input
                      type="date"
                      value={dailyFilters.dateFrom || ''}
                      onChange={handleDailyDateFromChange}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">
                      Дата по
                    </label>
                    <Input
                      type="date"
                      value={dailyFilters.dateTo || ''}
                      onChange={handleDailyDateToChange}
                    />
                  </div>
                </>
              ) : activeTab === 'monthly' ? (
                <>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">
                      Год
                    </label>
                    <Select
                      value={monthlyFilters.year?.toString() || ''}
                      onChange={handleMonthlyYearChange}
                    >
                      <SelectOption value="">Все годы</SelectOption>
                      {yearOptions.map((year) => (
                        <SelectOption key={year} value={year.toString()}>
                          {year}
                        </SelectOption>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">
                      Месяц
                    </label>
                    <Select
                      value={monthlyFilters.month?.toString() || ''}
                      onChange={handleMonthlyMonthChange}
                    >
                      <SelectOption value="">Все месяцы</SelectOption>
                      {MONTH_NAMES.map((name, idx) => (
                        <SelectOption key={idx + 1} value={(idx + 1).toString()}>
                          {name}
                        </SelectOption>
                      ))}
                    </Select>
                  </div>
                </>
              ) : (
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">
                    Статус
                  </label>
                  <Select
                    value={scheduleFilters.isActive === undefined ? '' : String(scheduleFilters.isActive)}
                    onChange={handleScheduleActiveFilter}
                  >
                    <SelectOption value="">Все</SelectOption>
                    <SelectOption value="true">Активные</SelectOption>
                    <SelectOption value="false">Неактивные</SelectOption>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Daily Limits Tab */}
        <TabsContent value="daily" className="space-y-4">
          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-2xl font-bold">{dailyStats.total}</p>
                    <p className="text-sm text-muted-foreground">Всего лимитов</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-2xl font-bold">{dailyStats.uniqueOrganizations}</p>
                    <p className="text-sm text-muted-foreground">Организаций</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-2xl font-bold">{dailyStats.uniquePositions}</p>
                    <p className="text-sm text-muted-foreground">Должностей</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-2xl font-bold">{dailyStats.totalCapacity}</p>
                    <p className="text-sm text-muted-foreground">Общий лимит</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Daily Table */}
          <Card>
            <CardHeader>
              <CardTitle>Дневные лимиты</CardTitle>
              <CardDescription>
                Максимальное количество сотрудников на смену по дате
              </CardDescription>
            </CardHeader>
            <CardContent>
              {dailyLoading ? (
                <LoadingTable rows={dailyFilters.limit || 20} />
              ) : dailyData && dailyData.data.length > 0 ? (
                <div className="space-y-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Дата</TableHead>
                        <TableHead>Организация</TableHead>
                        <TableHead>Должность</TableHead>
                        <TableHead>Отдел</TableHead>
                        <TableHead className="text-center">Макс. сотрудников</TableHead>
                        <TableHead>Создан</TableHead>
                        <TableHead className="text-right">Действия</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dailyData.data.map((limit) => (
                        <TableRow key={limit.id}>
                          <TableCell>
                            <span className="font-medium">{formatDate(limit.date)}</span>
                          </TableCell>
                          <TableCell>
                            {limit.organization?.name || 'Не указана'}
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">
                              {limit.position?.name || 'Не указана'}
                            </span>
                          </TableCell>
                          <TableCell>
                            {limit.department ? (
                              <span>{limit.department.name}</span>
                            ) : (
                              <span className="text-muted-foreground">Вся организация</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary" className="font-bold">
                              {limit.max_employees} чел.
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {formatDateTime(limit.created_at)}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center gap-2 justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditDaily(limit)}
                                title="Редактировать"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(limit.id, 'daily')}
                                title="Удалить"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {dailyData.totalPages > 1 && (
                    <div className="flex justify-center">
                      <Pagination
                        currentPage={dailyData.page}
                        totalPages={dailyData.totalPages}
                        onPageChange={handleDailyPageChange}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="mx-auto h-12 w-12 text-muted-foreground/50" />
                  <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                    Дневные лимиты не найдены
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Попробуйте изменить фильтры или добавьте новый лимит.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => setIsCreateDailyModalOpen(true)}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Добавить дневной лимит
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Monthly Limits Tab */}
        <TabsContent value="monthly" className="space-y-4">
          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-2xl font-bold">{monthlyStats.total}</p>
                    <p className="text-sm text-muted-foreground">Всего лимитов</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-2xl font-bold">{monthlyStats.uniqueOrganizations}</p>
                    <p className="text-sm text-muted-foreground">Организаций</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-2xl font-bold">{monthlyStats.uniquePositions}</p>
                    <p className="text-sm text-muted-foreground">Должностей</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-2xl font-bold">{monthlyStats.totalHours}</p>
                    <p className="text-sm text-muted-foreground">Общий лимит (часы)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Результат генерации лимитов смен из 1С */}
          {shiftGenError && (
            <div className="flex items-start justify-between gap-2 text-sm p-3 rounded-md border bg-red-50 border-red-300 text-red-900">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <p>{shiftGenError}</p>
              </div>
              <button onClick={() => setShiftGenError(null)}>
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          {shiftGenResult && (
            <div className="flex items-start justify-between gap-2 text-sm p-3 rounded-md border bg-muted/50">
              <div className="space-y-1">
                <p className="font-medium">
                  Лимиты смен из штатки 1С за {MONTH_NAMES[shiftGenResult.month - 1]} {shiftGenResult.year}:
                  {' '}создано {shiftGenResult.created}, обновлено {shiftGenResult.updated}
                  {shiftGenResult.skipped_manual > 0 && (
                    <>, сохранено ручных {shiftGenResult.skipped_manual}</>
                  )}
                </p>
                {(shiftGenResult.skipped_units.no_position > 0 ||
                  shiftGenResult.skipped_units.no_department > 0 ||
                  shiftGenResult.skipped_units.no_template > 0 ||
                  shiftGenResult.skipped_units.no_organization > 0) && (
                  <p className="flex items-center gap-1.5 text-amber-700">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    Не попали в расчёт штатные единицы: без должности — {shiftGenResult.skipped_units.no_position},
                    {' '}без подразделения — {shiftGenResult.skipped_units.no_department},
                    {' '}без графика — {shiftGenResult.skipped_units.no_template}
                    {shiftGenResult.skipped_units.no_organization > 0 && (
                      <>, без организации — {shiftGenResult.skipped_units.no_organization}</>
                    )}
                  </p>
                )}
              </div>
              <button onClick={() => setShiftGenResult(null)}>
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Monthly Table */}
          <Card>
            <CardHeader>
              <CardTitle>Месячные лимиты</CardTitle>
              <CardDescription>
                Максимальное количество часов и смен за месяц (лимит смен рассчитывается из штатки 1С)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {monthlyLoading ? (
                <LoadingTable rows={monthlyFilters.limit || 20} />
              ) : monthlyData && monthlyData.data.length > 0 ? (
                <div className="space-y-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Период</TableHead>
                        <TableHead>Организация</TableHead>
                        <TableHead>Должность</TableHead>
                        <TableHead>Отдел</TableHead>
                        <TableHead className="text-center">Макс. часов</TableHead>
                        <TableHead className="text-center">Макс. смен</TableHead>
                        <TableHead>Создан</TableHead>
                        <TableHead className="text-right">Действия</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {monthlyData.data.map((limit) => (
                        <TableRow key={limit.id}>
                          <TableCell>
                            <span className="font-medium">
                              {formatYearMonth(limit.year, limit.month)}
                            </span>
                          </TableCell>
                          <TableCell>
                            {limit.organization?.name || 'Не указана'}
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">
                              {limit.position?.name || 'Не указана'}
                            </span>
                          </TableCell>
                          <TableCell>
                            {limit.department ? (
                              <span>{limit.department.name}</span>
                            ) : (
                              <span className="text-muted-foreground">Вся организация</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {limit.max_hours > 0 ? (
                              <Badge variant="secondary" className="font-bold">
                                {limit.max_hours} ч.
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {limit.max_shifts != null ? (
                              <span className="inline-flex items-center gap-1">
                                <Badge variant="secondary" className="font-bold">
                                  {limit.max_shifts} см.
                                </Badge>
                                {limit.source === '1c_staffing' && (
                                  <Badge variant="outline" title="Рассчитано из штатного расписания 1С">
                                    1С
                                  </Badge>
                                )}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {formatDateTime(limit.created_at)}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center gap-2 justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditMonthly(limit)}
                                title="Редактировать"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(limit.id, 'monthly')}
                                title="Удалить"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {monthlyData.totalPages > 1 && (
                    <div className="flex justify-center">
                      <Pagination
                        currentPage={monthlyData.page}
                        totalPages={monthlyData.totalPages}
                        onPageChange={handleMonthlyPageChange}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Clock className="mx-auto h-12 w-12 text-muted-foreground/50" />
                  <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                    Месячные лимиты не найдены
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Попробуйте изменить фильтры или добавьте новый лимит.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => setIsCreateMonthlyModalOpen(true)}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Добавить месячный лимит
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Staffing Schedule Tab */}
        <TabsContent value="schedule" className="space-y-4">
          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <ClipboardList className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-2xl font-bold">{scheduleStats.total}</p>
                    <p className="text-sm text-muted-foreground">Всего позиций</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-2xl font-bold">{scheduleStats.activeCount}</p>
                    <p className="text-sm text-muted-foreground">Активных (на стр.)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-2xl font-bold">{scheduleStats.totalRates}</p>
                    <p className="text-sm text-muted-foreground">Ставок (на стр.)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-2xl font-bold">{formatMoney(scheduleStats.totalPayroll)}</p>
                    <p className="text-sm text-muted-foreground">ФОТ (на стр.)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Schedule Table */}
          <Card>
            <CardHeader>
              <CardTitle>Штатное расписание</CardTitle>
              <CardDescription>
                Данные из 1С — штатные единицы, ставки, оклады
              </CardDescription>
            </CardHeader>
            <CardContent>
              {scheduleLoading ? (
                <LoadingTable rows={scheduleFilters.limit || 20} />
              ) : scheduleData && scheduleData.data.length > 0 ? (
                <div className="space-y-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Код</TableHead>
                        <TableHead>Наименование</TableHead>
                        <TableHead>Подразделение</TableHead>
                        <TableHead>Должность</TableHead>
                        <TableHead className="text-center">Ставки</TableHead>
                        <TableHead>График</TableHead>
                        <TableHead className="text-right">Оклад</TableHead>
                        <TableHead className="text-right">ФОТ</TableHead>
                        <TableHead className="text-center">Статус</TableHead>
                        <TableHead>Дата</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {scheduleData.data.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <span className="font-mono text-sm">{item.code}</span>
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">{item.name}</span>
                          </TableCell>
                          <TableCell>
                            {item.department?.name || item.department_name}
                          </TableCell>
                          <TableCell>
                            {item.position?.name || item.position_name}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary" className="font-bold">
                              {item.rate_count}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {item.work_schedule || '—'}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            {formatMoney(item.salary)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatMoney(item.payroll_fund)}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant={item.is_active ? 'default' : 'outline'}>
                              {item.is_active ? 'Активна' : 'Неактивна'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {formatDate(item.date)}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {scheduleData.totalPages > 1 && (
                    <div className="flex justify-center">
                      <Pagination
                        currentPage={scheduleData.page}
                        totalPages={scheduleData.totalPages}
                        onPageChange={handleSchedulePageChange}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ClipboardList className="mx-auto h-12 w-12 text-muted-foreground/50" />
                  <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                    Штатное расписание не найдено
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Данные загружаются из 1С через синхронизацию.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <DailyStaffingLimitForm
        open={isCreateDailyModalOpen}
        onOpenChange={setIsCreateDailyModalOpen}
        mode="create"
      />

      <DailyStaffingLimitForm
        open={isEditDailyModalOpen}
        onOpenChange={(open) => {
          setIsEditDailyModalOpen(open)
          if (!open) setSelectedDailyLimit(null)
        }}
        limit={selectedDailyLimit}
        mode="edit"
      />

      <MonthlyStaffingLimitForm
        open={isCreateMonthlyModalOpen}
        onOpenChange={setIsCreateMonthlyModalOpen}
        mode="create"
      />

      <MonthlyStaffingLimitForm
        open={isEditMonthlyModalOpen}
        onOpenChange={(open) => {
          setIsEditMonthlyModalOpen(open)
          if (!open) setSelectedMonthlyLimit(null)
        }}
        limit={selectedMonthlyLimit}
        mode="edit"
      />

      <DeleteStaffingLimitDialog
        open={isDeleteModalOpen}
        onOpenChange={(open) => {
          setIsDeleteModalOpen(open)
          if (!open) setLimitToDelete(null)
        }}
        onConfirm={handleConfirmDelete}
        isLoading={deleteLimit.isPending}
      />

      <GenerateMonthlyLimitsDialog
        open={isGenerateModalOpen}
        onOpenChange={setIsGenerateModalOpen}
      />
    </div>
  )
}
